import { useState, useRef } from 'react';
import {
  SupplierDTO,
  useSupplier,
  useUpdateSupplierStatus,
} from '../../../hooks/useSuppliers';
import TitlePage from '../../../components/TitlePage';
import { Column, TableComponent } from '../../../components/TableComponent';
import CreateUpdate from './components/createUpdate';
import { Input } from '@/components/ui/input';
import Delete from './components/delete';
import { debounce } from 'lodash';
import LoadingCircle from '@/components/LoadingCircle';
import { useProfileContext } from '@/hooks/useProfileContext';
import { checkIsPermissionWithScreen, renderStatusText } from '@/lib/utils';
import { PaymentMethod, supplierStatus } from '@/api/suppliers';
import { Button } from '@/components/ui/button';
import { Filter } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

const ManageSuppliers = () => {
  const { profile } = useProfileContext();
  const searchRef = useRef<HTMLInputElement>(null);
  const taxCodeRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [taxCode, setTaxCode] = useState('');

  const { mutate: changeStatusSupplier } = useUpdateSupplierStatus();

  const { data, isLoading, isFetching } = useSupplier({
    search,
    taxCode,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingCircle />
      </div>
    );
  }

  const handleChangeStatus = (item: SupplierDTO) => {
    changeStatusSupplier({
      id: item.id,
      status: item.status === supplierStatus.ACTIVE ? false : true,
    });
  };

  const columns: Column<SupplierDTO>[] = [
    {
      header: 'STT',
      cell: (_item, index: number) => {
        return <div>{index + 1}</div>;
      },
    },
    {
      header: 'Tên nhà cung cấp',
      accessorKey: 'name',
      cell: (item) => {
        return (
          <div className="font-medium text-luxury-900 w-[300px] whitespace-pre-line break-words">
            {item.name}
          </div>
        );
      },
    },
    {
      header: 'Trạng thái',
      accessorKey: 'status',
      cell: (item) => {
        return (
          <div className="flex items-center gap-2">
            <Switch
              defaultChecked={item.status === supplierStatus.ACTIVE}
              onCheckedChange={() => {
                handleChangeStatus(item);
              }}
            />

            <span className={renderStatusText(item.status).className}>
              {renderStatusText(item.status).label}
            </span>
          </div>
        );
      },
    },
    {
      header: 'Mã số thuế',
      accessorKey: 'taxCode',
    },
    {
      header: 'Địa chỉ',
      accessorKey: 'address',
      cell: (item) => {
        return (
          <div className="w-[300px] whitespace-pre-line break-words">
            {item.address}
          </div>
        );
      },
    },
    {
      header: 'Số điện thoại',
      accessorKey: 'phone',
    },
    {
      header: 'Email',
      accessorKey: 'email',
    },
    {
      header: 'Ngân hàng',
      accessorKey: 'bankName',
    },
    {
      header: 'Số tài khoản',
      accessorKey: 'bankAccountNumber',
    },
    {
      header: 'Chủ tài khoản',
      accessorKey: 'bankAccountHolder',
    },
    {
      header: 'Phương thức thanh toán',
      accessorKey: 'paymentMethod',
      cell: (item) => {
        const paymentMethodMap = {
          [PaymentMethod.BANK_TRANSFER]: {
            text: 'Chuyển khoản',
            color: 'bg-blue-100 text-blue-800',
          },
          [PaymentMethod.CASH]: {
            text: 'Tiền mặt',
            color: 'bg-green-100 text-green-800',
          },
          [PaymentMethod.CREDIT]: {
            text: 'Tín dụng',
            color: 'bg-purple-100 text-purple-800',
          },
        };
        return item.paymentMethod ? (
          <div
            className={`px-2 py-1 rounded-full text-sm font-medium inline-block ${
              paymentMethodMap[item.paymentMethod as PaymentMethod].color
            }`}
          >
            {paymentMethodMap[item.paymentMethod as PaymentMethod].text}
          </div>
        ) : null;
      },
    },
    {
      header: 'Người tạo',
      accessorKey: 'creator',
      cell: (item) => {
        return <div>{item.creator.name}</div>;
      },
    },
    {
      header: 'Hành động',
      cell: (item) => {
        return (
          <div className="flex items-center gap-2">
            {checkIsPermissionWithScreen(
              'supplier',
              'update',
              profile?.role?.permissions
            ) && <CreateUpdate supplier={item} />}
            {checkIsPermissionWithScreen(
              'supplier',
              'delete',
              profile?.role?.permissions
            ) && <Delete supplier={item} />}
          </div>
        );
      },
    },
  ];

  const debouncedSearch = debounce((value: string) => {
    setSearch(value);
  }, 500);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  return (
    <div className="luxury-theme min-h-screen">
      <div className="flex items-center justify-between bg-white p-4 border-b border-luxury-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="text-2xl  text-luxury-900">
            <TitlePage />
          </div>
        </div>
        {checkIsPermissionWithScreen(
          'supplier',
          'create',
          profile?.role?.permissions
        ) && <CreateUpdate />}
      </div>

      <div className="flex items-center justify-end bg-white p-4 border-b border-luxury-200 shadow-sm gap-4">
        <div className="flex items-center gap-2">
          <Input
            ref={searchRef}
            placeholder="Tìm kiếm theo tên..."
            className="w-[250px] luxury-input"
            onChange={handleSearch}
          />
          <Input
            ref={taxCodeRef}
            placeholder="Tìm kiếm theo mã số thuế..."
            className="w-[250px] luxury-input"
            onChange={(e) => setTaxCode(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="luxury-button-outline"
            onClick={() => {
              setSearch('');
              setTaxCode('');
              if (searchRef.current) {
                searchRef.current.value = '';
              }
              if (taxCodeRef.current) {
                taxCodeRef.current.value = '';
              }
            }}
          >
            <Filter className="w-4 h-4 mr-2" />
            Xoá lọc
          </Button>
        </div>
      </div>

      <div className="p-4 relative">
        {isFetching && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
            <LoadingCircle />
          </div>
        )}
        <TableComponent
          data={data?.suppliers || []}
          columns={columns}
          pageSize={0}
          totalPages={0}
          onPageChange={() => {}}
          currentPage={0}
          isPagination={false}
          className="luxury-table"
        />
      </div>
    </div>
  );
};

export default ManageSuppliers;
